import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, User, Shield, Mail, Calendar, Clock, Edit2, X, Loader2, AlertCircle, RefreshCw, Plus, Save, LayoutGrid, List, Lock, Unlock, Key, ToggleLeft, ToggleRight, CheckCircle, XCircle, UserCheck, UserX, Smartphone, UserPlus } from 'lucide-react';
import { userService, employeeService } from '../services';
import type { User as UserType, UserCreateDto, UserUpdateDto, Role, Employee } from '../services';
import { usePermission } from '../context/PermissionContext';

interface UserFormData {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  employeeId: number;
  roleIds: number[];
}

interface FormModalProps {
  title: string;
  onSubmit: () => void;
  onClose: () => void;
  isEdit?: boolean;
  formData: UserFormData;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onRoleChange: (roleId: number) => void;
  roles: Role[];
  employees: Employee[];
  formError: string | null;
  saving: boolean;
}

const FormModal: React.FC<FormModalProps> = ({
  title, onSubmit, onClose, isEdit = false, formData, onInputChange, onRoleChange, roles, employees, formError, saving
}) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-8 py-6 flex justify-between items-center">
          <h2 className="text-xl font-black text-slate-800">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-8 space-y-6">
          {formError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{formError}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('users.username')} *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={onInputChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('users.usernamePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('common.email')}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={onInputChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('users.emailPlaceholder')}
              />
            </div>
          </div>

          {!isEdit && (
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('users.password')} *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={onInputChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="********"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.firstName')}</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={onInputChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('employees.firstName')}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.lastName')}</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={onInputChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('employees.lastName')}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('users.linkedEmployee')}</label>
            <select
              name="employeeId"
              value={formData.employeeId}
              onChange={onInputChange}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>{t('users.selectEmployee')}</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{t('users.roles')}</label>
            <div className="flex flex-wrap gap-2">
              {roles.map(role => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => onRoleChange(role.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    formData.roleIds.includes(role.id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {role.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-100 px-8 py-6 flex gap-4">
          <button onClick={onClose} className="flex-1 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-100 transition-all">{t('common.cancel')}</button>
          <button onClick={onSubmit} disabled={saving} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

const emptyFormData: UserFormData = {
  username: '',
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  employeeId: 0,
  roleIds: [],
};

const Users = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserType[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showToggleStatusModal, setShowToggleStatusModal] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [showNfcModal, setShowNfcModal] = useState(false);
  const [nfcEmployeeId, setNfcEmployeeId] = useState<number>(0);
  const [nfcUsername, setNfcUsername] = useState('');
  const [nfcPassword, setNfcPassword] = useState('');
  const [formData, setFormData] = useState<UserFormData>(emptyFormData);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersRes, rolesRes, employeesRes] = await Promise.all([
        userService.getAll(),
        userService.getRoles(),
        employeeService.getAll()
      ]);

      if (usersRes.success) setUsers(usersRes.data);
      if (rolesRes.success) setRoles(rolesRes.data);
      if (employeesRes.success) setEmployees(employeesRes.data);
    } catch (err: any) {
      console.error('Load error:', err);
      setError(err.response?.data?.message || t('users.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const searchScope = `${user.username} ${user.email} ${user.fullName || ''}`.toLowerCase();
      const matchesSearch = searchScope.includes(searchTerm.toLowerCase());
      const matchesRole = selectedRole === 'all' || user.roles.some(r => r.id === parseInt(selectedRole));
      const matchesStatus = selectedStatus === 'all' ||
        (selectedStatus === 'active' && user.isActive && !user.isLocked) ||
        (selectedStatus === 'inactive' && !user.isActive) ||
        (selectedStatus === 'locked' && user.isLocked);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, selectedRole, selectedStatus]);

  const handleCreate = () => {
    setFormData(emptyFormData);
    setFormError(null);
    setShowCreateModal(true);
  };

  const handleEdit = (user: UserType) => {
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      employeeId: user.employeeId || 0,
      roleIds: user.roles.map(r => r.id),
    });
    setFormError(null);
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleResetPassword = (user: UserType) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const handleToggleStatus = (user: UserType) => {
    setSelectedUser(user);
    setShowToggleStatusModal(true);
  };

  const handleLockToggle = (user: UserType) => {
    setSelectedUser(user);
    setShowLockModal(true);
  };

  const submitCreate = async () => {
    if (!formData.username) {
      setFormError(t('users.usernameRequired'));
      return;
    }
    if (!formData.password) {
      setFormError(t('users.passwordRequired'));
      return;
    }

    try {
      setSaving(true);
      setFormError(null);

      const dto: UserCreateDto = {
        username: formData.username,
        email: formData.email || undefined,
        password: formData.password,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        employeeId: formData.employeeId || undefined,
        roleIds: formData.roleIds.length > 0 ? formData.roleIds : undefined,
      };

      const res = await userService.create(dto);
      if (res.success) {
        setShowCreateModal(false);
        await loadData();
      } else {
        setFormError(res.message || t('users.createFailed'));
      }
    } catch (err: any) {
      setFormError(err.response?.data?.message || t('users.createError'));
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!selectedUser) return;
    if (!formData.username) {
      setFormError(t('users.usernameRequired'));
      return;
    }

    try {
      setSaving(true);
      setFormError(null);

      const dto: UserUpdateDto = {
        username: formData.username,
        email: formData.email || undefined,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        employeeId: formData.employeeId || undefined,
        roleIds: formData.roleIds,
      };

      const res = await userService.update(selectedUser.id, dto);
      if (res.success) {
        setShowEditModal(false);
        setSelectedUser(null);
        await loadData();
      } else {
        setFormError(res.message || t('users.updateFailed'));
      }
    } catch (err: any) {
      setFormError(err.response?.data?.message || t('users.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const submitResetPassword = async () => {
    if (!selectedUser || !newPassword) return;

    try {
      setSaving(true);
      const res = await userService.resetPassword(selectedUser.id, newPassword);
      if (res.success) {
        setShowResetPasswordModal(false);
        setSelectedUser(null);
        setNewPassword('');
      }
    } catch (err: any) {
      console.error('Reset password error:', err);
    } finally {
      setSaving(false);
    }
  };

  const submitToggleStatus = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      const res = await userService.toggleStatus(selectedUser.id);
      if (res.success) {
        setShowToggleStatusModal(false);
        setSelectedUser(null);
        await loadData();
      }
    } catch (err: any) {
      console.error('Toggle status error:', err);
    } finally {
      setSaving(false);
    }
  };

  const submitLockToggle = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      if (selectedUser.isLocked) {
        await userService.unlockUser(selectedUser.id);
      } else {
        await userService.lockUser(selectedUser.id);
      }
      setShowLockModal(false);
      setSelectedUser(null);
      await loadData();
    } catch (err: any) {
      console.error('Lock toggle error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleNfc = async (user: UserType) => {
    try {
      setSaving(true);
      const res = await userService.toggleNfc(user.id);
      if (res.success) {
        await loadData();
      }
    } catch (err: any) {
      console.error('NFC toggle error:', err);
    } finally {
      setSaving(false);
    }
  };

  const hasNfcRole = (user: UserType) => {
    return user.roles.some(r => r.name.toLowerCase() === 'nfc_operator');
  };

  // Personelden NFC kullanici olustur
  const availableEmployeesForNfc = useMemo(() => {
    const usedEmployeeIds = users.map(u => u.employeeId).filter(Boolean);
    return employees.filter(e => !usedEmployeeIds.includes(e.employeeId || e.id));
  }, [users, employees]);

  const handleNfcFromEmployee = () => {
    setNfcEmployeeId(0);
    setNfcUsername('');
    setNfcPassword('');
    setFormError(null);
    setShowNfcModal(true);
  };

  const handleNfcEmployeeSelect = (empId: number) => {
    setNfcEmployeeId(empId);
    const emp = employees.find(e => (e.employeeId || e.id) === empId);
    if (emp) {
      // Otomatik kullanici adi onerisi: ad.soyad
      const suggested = `${(emp.firstName || '').toLowerCase()}.${(emp.lastName || '').toLowerCase()}`.replace(/\s+/g, '');
      setNfcUsername(suggested);
    }
  };

  const submitNfcUser = async () => {
    if (!nfcEmployeeId) { setFormError(t('users.selectEmployeeRequired')); return; }
    if (!nfcUsername) { setFormError(t('users.usernameRequired')); return; }
    if (!nfcPassword) { setFormError(t('users.passwordRequired')); return; }

    try {
      setSaving(true);
      setFormError(null);

      const emp = employees.find(e => (e.employeeId || e.id) === nfcEmployeeId);
      const nfcRole = roles.find(r => r.name.toLowerCase() === 'nfc_operator');

      const dto: UserCreateDto = {
        username: nfcUsername,
        password: nfcPassword,
        firstName: emp?.firstName,
        lastName: emp?.lastName,
        employeeId: nfcEmployeeId,
        roleIds: nfcRole ? [nfcRole.id] : undefined,
      };

      const res = await userService.create(dto);
      if (res.success) {
        setShowNfcModal(false);
        await loadData();
      } else {
        setFormError(res.message || t('users.createFailed'));
      }
    } catch (err: any) {
      setFormError(err.response?.data?.message || t('users.genericError'));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'employeeId' ? parseInt(value) || 0 : value
    }));
  };

  const handleRoleChange = (roleId: number) => {
    setFormData(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(id => id !== roleId)
        : [...prev.roleIds, roleId]
    }));
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName.toLowerCase()) {
      case 'admin': return 'bg-red-100 text-red-700 border-red-200';
      case 'manager': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'hr': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'nfc_operator': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">{t('common.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">{t('common.error')}</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={loadData} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
          {t('users.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t('users.title')}</h1>
          <p className="text-slate-500 text-sm font-medium">{t('users.totalUsers', { count: users.length })}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-medium">
            <RefreshCw size={16} /> {t('users.refresh')}
          </button>
          <button onClick={handleNfcFromEmployee} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium">
            <Smartphone size={16} /> {t('users.createNfcUser')}
          </button>
          {hasPermission('canCreateEmployees') && (
            <button onClick={handleCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              <Plus size={16} /> {t('users.newUser')}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder={t('users.searchPlaceholder')}
            className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 text-sm font-medium transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-600 outline-none focus:ring-4 focus:ring-blue-500/10 min-w-[150px] transition-all cursor-pointer">
            <option value="all">{t('users.allRoles')}</option>
            {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-600 outline-none focus:ring-4 focus:ring-blue-500/10 min-w-[150px] transition-all cursor-pointer">
            <option value="all">{t('users.allStatuses')}</option>
            <option value="active">{t('users.active')}</option>
            <option value="inactive">{t('users.inactive')}</option>
            <option value="locked">{t('users.locked')}</option>
          </select>
          <div className="flex bg-slate-50 border border-slate-200 rounded-xl p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'card' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredUsers.map(user => (
            <div key={user.id} className="bg-white rounded-[2rem] p-6 border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-300 group relative overflow-hidden">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-[1.8rem] bg-slate-100 border-4 border-white shadow-md flex items-center justify-center text-slate-600 font-black text-xl group-hover:scale-105 transition-transform duration-500">
                    <User size={32} />
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-4 border-white shadow-lg ${user.isLocked ? 'bg-red-500' : user.isActive ? 'bg-green-500' : 'bg-slate-400'}`}></div>
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-800">{user.fullName || user.username}</h3>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">@{user.username}</p>
                </div>
                <div className="flex flex-wrap justify-center gap-1">
                  {user.roles.map(role => (
                    <span key={role.id} className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border ${getRoleColor(role.name)}`}>
                      {role.name}
                    </span>
                  ))}
                </div>
                <div className="w-full pt-4 border-t border-slate-50 space-y-2">
                  <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                    <Mail size={12} className="text-slate-300" />
                    <span className="truncate">{user.email || t('users.noEmail')}</span>
                  </div>
                  {user.lastLoginDate && (
                    <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                      <Clock size={12} className="text-slate-300" />
                      <span>{t('users.lastLogin')}: {new Date(user.lastLoginDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                <div className="w-full pt-2 flex gap-2">
                  <button onClick={() => handleEdit(user)} className="flex-1 bg-slate-50 text-slate-400 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 border border-slate-100 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 transition-all">
                    <Edit2 size={12} /> {t('common.edit')}
                  </button>
                  <button onClick={() => handleResetPassword(user)} className="bg-slate-50 text-slate-400 py-2 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center border border-slate-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all">
                    <Key size={12} />
                  </button>
                  <button onClick={() => handleLockToggle(user)} className={`bg-slate-50 py-2 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center border border-slate-100 transition-all ${user.isLocked ? 'text-red-400 hover:bg-green-50 hover:text-green-600 hover:border-green-200' : 'text-slate-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200'}`}>
                    {user.isLocked ? <Unlock size={12} /> : <Lock size={12} />}
                  </button>
                  <button
                    onClick={() => handleToggleNfc(user)}
                    disabled={saving}
                    className={`py-2 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-1 border transition-all ${
                      hasNfcRole(user)
                        ? 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                        : 'bg-slate-50 text-slate-400 border-slate-100 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200'
                    }`}
                    title={hasNfcRole(user) ? t('users.removeNfc') : t('users.grantNfc')}
                  >
                    <Smartphone size={12} />
                    {hasNfcRole(user) ? 'NFC' : ''}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('users.user')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('common.email')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('users.roles')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('users.lastLogin')}</th>
                <th className="text-center px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('common.status')}</th>
                <th className="text-right px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 border-2 border-white shadow-sm flex items-center justify-center text-slate-500">
                        <User size={18} />
                      </div>
                      <div>
                        <div className="font-bold text-slate-800">{user.fullName || user.username}</div>
                        <div className="text-[10px] text-slate-400 font-medium">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-600">{user.email || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map(role => (
                        <span key={role.id} className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border ${getRoleColor(role.name)}`}>
                          {role.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-500">
                      {user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleDateString('tr-TR') : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                      user.isLocked
                        ? 'bg-red-50 text-red-600 border border-red-100'
                        : user.isActive
                        ? 'bg-green-50 text-green-600 border border-green-100'
                        : 'bg-slate-100 text-slate-400 border border-slate-200'
                    }`}>
                      {user.isLocked ? t('users.locked') : user.isActive ? t('users.active') : t('users.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(user)} className="p-2 text-slate-300 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all" title={t('common.edit')}>
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleResetPassword(user)} className="p-2 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title={t('users.resetPassword')}>
                        <Key size={16} />
                      </button>
                      <button onClick={() => handleToggleStatus(user)} className={`p-2 rounded-lg transition-all ${user.isActive ? 'text-slate-300 hover:text-orange-600 hover:bg-orange-50' : 'text-slate-300 hover:text-green-600 hover:bg-green-50'}`} title={user.isActive ? t('users.deactivate') : t('users.activate')}>
                        {user.isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                      </button>
                      <button onClick={() => handleLockToggle(user)} className={`p-2 rounded-lg transition-all ${user.isLocked ? 'text-red-400 hover:text-green-600 hover:bg-green-50' : 'text-slate-300 hover:text-red-600 hover:bg-red-50'}`} title={user.isLocked ? t('users.unlock') : t('users.lock')}>
                        {user.isLocked ? <Unlock size={16} /> : <Lock size={16} />}
                      </button>
                      <button
                        onClick={() => handleToggleNfc(user)}
                        disabled={saving}
                        className={`p-2 rounded-lg transition-all ${
                          hasNfcRole(user)
                            ? 'text-emerald-500 hover:text-red-600 hover:bg-red-50'
                            : 'text-slate-300 hover:text-emerald-600 hover:bg-emerald-50'
                        }`}
                        title={hasNfcRole(user) ? t('users.removeNfc') : t('users.grantNfc')}
                      >
                        <Smartphone size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <FormModal
          title={t('users.addNewUser')}
          onSubmit={submitCreate}
          onClose={() => setShowCreateModal(false)}
          formData={formData}
          onInputChange={handleInputChange}
          onRoleChange={handleRoleChange}
          roles={roles}
          employees={employees}
          formError={formError}
          saving={saving}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <FormModal
          title={t('users.editUser')}
          onSubmit={submitEdit}
          onClose={() => { setShowEditModal(false); setSelectedUser(null); }}
          isEdit={true}
          formData={formData}
          onInputChange={handleInputChange}
          onRoleChange={handleRoleChange}
          roles={roles}
          employees={employees}
          formError={formError}
          saving={saving}
        />
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">{t('users.resetPassword')}</h3>
              <p className="text-slate-500 mb-6">
                {t('users.setNewPasswordFor', { name: selectedUser.fullName || selectedUser.username })}
              </p>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('users.newPassword')}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 mb-6"
              />
              <div className="flex gap-4">
                <button onClick={() => { setShowResetPasswordModal(false); setSelectedUser(null); }} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all">{t('common.cancel')}</button>
                <button onClick={submitResetPassword} disabled={saving || !newPassword} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all disabled:opacity-50">
                  {saving ? t('common.loading') : t('users.resetPassword')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Status Modal */}
      {showToggleStatusModal && selectedUser && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${selectedUser.isActive ? 'bg-orange-100' : 'bg-green-100'}`}>
                {selectedUser.isActive ? <UserX className="w-8 h-8 text-orange-600" /> : <UserCheck className="w-8 h-8 text-green-600" />}
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">
                {selectedUser.isActive ? t('users.deactivateUser') : t('users.activateUser')}
              </h3>
              <p className="text-slate-500 mb-6">
                {t('users.confirmToggleStatus', { name: selectedUser.fullName || selectedUser.username, action: selectedUser.isActive ? t('users.deactivate') : t('users.activate') })}
              </p>
              <div className="flex gap-4">
                <button onClick={() => { setShowToggleStatusModal(false); setSelectedUser(null); }} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all">{t('common.cancel')}</button>
                <button onClick={submitToggleStatus} disabled={saving} className={`flex-1 py-3 text-white rounded-xl font-bold transition-all disabled:opacity-50 ${selectedUser.isActive ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`}>
                  {saving ? t('common.loading') : selectedUser.isActive ? t('users.deactivate') : t('users.activate')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lock/Unlock Modal */}
      {showLockModal && selectedUser && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${selectedUser.isLocked ? 'bg-green-100' : 'bg-red-100'}`}>
                {selectedUser.isLocked ? <Unlock className="w-8 h-8 text-green-600" /> : <Lock className="w-8 h-8 text-red-600" />}
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">
                {selectedUser.isLocked ? t('users.unlockAccount') : t('users.lockAccount')}
              </h3>
              <p className="text-slate-500 mb-6">
                {t('users.confirmLockToggle', { name: selectedUser.fullName || selectedUser.username, action: selectedUser.isLocked ? t('users.unlock') : t('users.lock') })}
              </p>
              <div className="flex gap-4">
                <button onClick={() => { setShowLockModal(false); setSelectedUser(null); }} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all">{t('common.cancel')}</button>
                <button onClick={submitLockToggle} disabled={saving} className={`flex-1 py-3 text-white rounded-xl font-bold transition-all disabled:opacity-50 ${selectedUser.isLocked ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                  {saving ? t('common.loading') : selectedUser.isLocked ? t('users.unlock') : t('users.lock')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredUsers.length === 0 && (
        <div className="py-20 text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-[2rem] flex items-center justify-center mx-auto text-slate-300 mb-6"><Search size={40} /></div>
          <h3 className="text-lg font-black text-slate-800">{t('users.noResults')}</h3>
          <p className="text-slate-500 text-sm font-medium mt-2">{t('users.noResultsDescription')}</p>
        </div>
      )}

      {/* NFC Kullanici Olustur Modal */}
      {showNfcModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-8 py-6 flex justify-between items-center rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-800">{t('users.createNfcUser')}</h2>
                  <p className="text-xs text-slate-500">{t('users.createNfcUserDesc')}</p>
                </div>
              </div>
              <button onClick={() => setShowNfcModal(false)} className="p-2 hover:bg-slate-100 rounded-full"><X size={20} /></button>
            </div>
            <div className="p-8 space-y-5">
              {formError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{formError}</div>}

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('users.selectEmployeeLabel')} *</label>
                <select
                  value={nfcEmployeeId}
                  onChange={(e) => handleNfcEmployeeSelect(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 text-sm"
                >
                  <option value={0}>-- {t('users.selectEmployee')} --</option>
                  {availableEmployeesForNfc.map(emp => (
                    <option key={emp.employeeId || emp.id} value={emp.employeeId || emp.id}>
                      {emp.firstName} {emp.lastName} {emp.departmentName ? `(${emp.departmentName})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('users.username')} *</label>
                <input
                  type="text"
                  value={nfcUsername}
                  onChange={(e) => setNfcUsername(e.target.value)}
                  placeholder={t('users.usernamePlaceholder')}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('users.password')} *</label>
                <input
                  type="text"
                  value={nfcPassword}
                  onChange={(e) => setNfcPassword(e.target.value)}
                  placeholder={t('users.loginPassword')}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 text-sm"
                />
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Smartphone size={18} className="text-emerald-600 mt-0.5" />
                  <div className="text-xs text-emerald-700">
                    <p className="font-bold mb-1">{t('users.nfcOperatorRole')}</p>
                    <p>{t('users.nfcOperatorDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowNfcModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 text-sm font-bold"
                >
                  Iptal
                </button>
                <button
                  onClick={submitNfcUser}
                  disabled={saving}
                  className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
                  {t('users.create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
